import { init, replayIntegration, browserTracingIntegration } from '@sentry/remix';
/**
 * By default, Remix will handle hydrating your app on the client for you.
 * You are free to delete this file if you'd like to, but if you ever want it revealed again, you can run `npx remix reveal` ✨
 * For more information, see https://remix.run/file-conventions/entry.client
 */

import { RemixBrowser, useLocation, useMatches } from '@remix-run/react';
import { startTransition, useEffect } from 'react';
import { hydrateRoot } from 'react-dom/client';

// Enable detailed React hydration error logging in development
if (process.env.NODE_ENV === 'development') {
  // @ts-ignore - Enable React's detailed hydration logging
  window.__REACT_DEVTOOLS_GLOBAL_HOOK__?.on?.('root', (root: any) => {
    console.log('🔍 React Root Hydrating:', root);
  });
}

// Only initialize Sentry in production to reduce development noise
if (process.env.NODE_ENV === 'production') {
  init({
    dsn: 'https://2e844d73ffa53a1ff2155f40ea00a109@o4510132427816960.ingest.us.sentry.io/4510132428996608',
    tracesSampleRate: 0.1, // Reduced from 1 to 0.1 (90% reduction)
    enableLogs: false, // Reduced from true to false

    integrations: [
      browserTracingIntegration({
        useEffect,
        useLocation,
        useMatches,
      }),
      replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    replaysSessionSampleRate: 0.02, // Reduced from 0.1 to 0.02 (80% reduction)
    replaysOnErrorSampleRate: 0.3, // Reduced from 1 to 0.3 (70% reduction)

    // Filter out noise from bots and aborted requests
    beforeSend(event) {
      const errorType = event.exception?.values?.[0]?.type;
      const errorMessage = event.exception?.values?.[0]?.value || '';

      // Filter known noise (keep debugging capability for new issues)
      if (errorType === 'AbortError') return null;
      if (errorMessage.includes('invalid time zone: -07:00')) return null;
      if (errorMessage.includes('Load failed (a.klaviyo.com)')) return null;
      if (errorMessage.includes('Load failed (analytics.mantis.marketing)')) return null;

      // Keep everything else for debugging
      return event;
    },
  });
}

// PATCH FIX: Clear invalid san-bernardino-dispensary store selection
const clearInvalidStore = () => {
  const invalidStore = 'san-bernardino-dispensary';

  // Check and clear cookie
  const cookies = document.cookie.split(';');
  const selectedStoreCookie = cookies.find((cookie) => cookie.trim().startsWith('selectedStore='));

  if (selectedStoreCookie && selectedStoreCookie.includes(invalidStore)) {
    document.cookie = 'selectedStore=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  }

  // Check and clear localStorage
  const localStorageValue = localStorage.getItem('selectedStore');
  if (localStorageValue === invalidStore) {
    localStorage.removeItem('selectedStore');
  }

  // Reload page if either was cleared
  if (
    (selectedStoreCookie && selectedStoreCookie.includes(invalidStore)) ||
    localStorageValue === invalidStore
  ) {
    window.location.reload();
    return true;
  }
  return false;
};

// CRITICAL FIX: Run hydration FIRST, then clear invalid store
// Running clearInvalidStore() before hydration causes hydration mismatches!
startTransition(() => {
  hydrateRoot(document, <RemixBrowser />);
});

// Run the patch fix AFTER hydration completes to avoid hydration errors
setTimeout(() => {
  clearInvalidStore();
}, 0);
